import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Modal, Button } from 'react-bootstrap';
import { FaPlayCircle, FaTimes } from 'react-icons/fa';
import { useAppSelector } from '../../store/hooks';
import jwtDecode from 'jwt-decode';
import { parseDataBRParaDate } from '../../services/utilitarios';
import { estaEmPeriodoDeTeste } from '../../services/seguranca';

// --- Animações ---
const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(255, 107, 107, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 107, 107, 0); }
`;

const slideDown = keyframes`
  from { transform: translateY(-50px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// --- Estilos ---
const StyledModal = styled(Modal)`
  .modal-content {
    border: none;
    border-radius: 20px;
    overflow: hidden;
    animation: ${slideDown} 0.5s ease-out;
    background: #fff;
    box-shadow: 0 15px 35px rgba(0,0,0,0.2);
  }
`;

const HeaderBanner = styled.div`
  background: linear-gradient(135deg, #1F467E 0%, #0d2546 100%);
  padding: 30px 20px;
  text-align: center;
  position: relative;
  color: white;
`;

const Title = styled.h2`
  font-weight: 800;
  margin: 0;
  font-size: 1.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #FFFFFF; /* Dourado para destaque */
  
`;

const SubTitle = styled.p`
  margin-top: 10px;
  opacity: 0.9;
  font-size: 1rem;
`;

const BodyContent = styled.div`
  padding: 30px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const InfoText = styled.p`
  color: #555;
  font-size: 1.1rem;
  line-height: 1.5;
  margin-bottom: 10px;

  strong {
    color: #1F467E;
  }
`;

const CTAButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(45deg, #ff6b6b, #ee5253);
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
  padding: 15px 40px;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s;
  animation: ${pulse} 2s infinite;
  border: 2px solid white;
  box-shadow: 0 10px 20px rgba(238, 82, 83, 0.4);
  cursor: pointer;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 25px rgba(238, 82, 83, 0.6);
    color: white;
    text-decoration: none;
  }

  svg {
    font-size: 1.5rem;
  }
`;

const CloseButtonAbsolute = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: rgba(255,255,255,0.4);
  }
`;

interface TokenPayload {
    dataCadastro?: string;
    [key: string]: any;
}

const ModalTreinamento: React.FC = () => {
    const [show, setShow] = useState(false);
    const [token, setToken] = useState<string>(useAppSelector(state => state.token));

    useEffect(() => {
        verificarDataCadastro();
    }, [token]);

    const verificarDataCadastro = () => {
        // Se já viu o modal nesta sessão, não mostra de novo
        if (sessionStorage.getItem('modalTreinamentoVisto')) return;

        // No plano de teste o dashboard abre o "Primeiros Passos"; o treinamento fica no botão do topo.
        if (token && estaEmPeriodoDeTeste(token)) return;

        // Se o tour guiado já foi visto, não mostra o modal (evitar popup duplo)
        if (localStorage.getItem('locasmart_dashboard_tour_seen')) return;

        if (token) {
            try {
                const decoded = jwtDecode<TokenPayload>(token);
                
                // Verifica se existe a claim dataCadastro
                if (decoded.dataCadastro) {
                    const dataCadastro = parseDataBRParaDate(decoded.dataCadastro);
                    
                    if (dataCadastro) {
                        const hoje = new Date();
                        // Diferença em milissegundos
                        const diffTime = Math.abs(hoje.getTime() - dataCadastro.getTime());
                        // Diferença em dias
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        // Regra: Mostrar se tiver 7 dias ou menos de cadastro
                        if (diffDays <= 7) {
                            // Pequeno delay para animação de entrada da tela Home não brigar com o modal
                            setTimeout(() => setShow(true), 1500);
                        }
                    }
                }
            } catch (error) {
                console.error("Erro ao decodificar token para modal de treinamento", error);
            }
        }
    };

    const handleClose = () => {
        setShow(false);
        // Marca na sessão para não abrir novamente até o usuário fechar o navegador/fazer logout
        sessionStorage.setItem('modalTreinamentoVisto', 'true');
    };

    return (
        <StyledModal 
            show={show} 
            onHide={handleClose} 
            centered 
            size="lg"
            backdrop="static"
        >
            <HeaderBanner>
                <CloseButtonAbsolute onClick={handleClose}>
                    <FaTimes />
                </CloseButtonAbsolute>
                <Title>🚀 Comece com o Pé Direito!</Title>
            </HeaderBanner>

            <BodyContent>
                <InfoText>
                    Percebemos que você iniciou sua jornada no <strong>LocaSmart Pro</strong> recentemente.
                    <br />
                    Para garantir que você aproveite <strong>100% dos recursos</strong>, assista ao nosso treinamento completo.
                </InfoText>

                <CTAButton 
                    href="https://treinamento.locasmartpro.com.br/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={handleClose}
                >
                    <FaPlayCircle />
                    ASSISTIR TREINAMENTO AGORA
                </CTAButton>
                
                <small className="text-muted mt-2">
                    <strong>É rápido, prático e vai transformar a gestão da sua locadora.</strong>
                </small>
            </BodyContent>
        </StyledModal>
    );
};

export default ModalTreinamento;